import { integer, pgTable, text, uniqueIndex, uuid } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

import { patientsTable } from "./patientsSchema";
import { customTimestamp } from "./utils/types";

export const cesSurveyTriggerPointEnumSchema = z.enum([
  "CONSENT_FORM_SIGNED",
  "HOUSEHOLD_MEMBER_ADDED",
  "INCOME_ADDED",
]);

export type CesSurveyTriggerPointEnumType = z.infer<
  typeof cesSurveyTriggerPointEnumSchema
>;

// 1 = "Very hard" through 5 = "Very easy"
export const cesSurveyScoreSchema = z.number().int().min(1).max(5);

export const cesSurveysTable = pgTable(
  "ces_surveys",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    patientId: uuid("patient_id")
      .references(() => patientsTable.id, { onDelete: "cascade" })
      .notNull(),
    triggerPoint: text("trigger_point")
      .$type<CesSurveyTriggerPointEnumType>()
      .notNull(),
    // Null when the prompt was shown but skipped
    score: integer("score"),
    feedback: text("feedback"),
    createdAt: customTimestamp("created_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString()),
    updatedAt: customTimestamp("updated_at")
      .notNull()
      .$defaultFn(() => new Date().toISOString())
      .$onUpdateFn(() => new Date().toISOString()),
  },
  // One survey per patient, not one per trigger point
  table => [
    uniqueIndex("ces_surveys_patient_id_unique_index").on(table.patientId),
  ],
);

export const cesSurveySchema = createSelectSchema(cesSurveysTable, {
  triggerPoint: cesSurveyTriggerPointEnumSchema,
  score: cesSurveyScoreSchema.nullable(),
});
export type CesSurveyType = z.infer<typeof cesSurveySchema>;

export const insertCesSurveySchema = createInsertSchema(cesSurveysTable, {
  patientId: z.uuid(),
  triggerPoint: cesSurveyTriggerPointEnumSchema,
  score: cesSurveyScoreSchema.nullish(),
}).omit({
  createdAt: true,
  updatedAt: true,
});
export type InsertCesSurveyType = z.infer<typeof insertCesSurveySchema>;

// Only the patient's answer is mutable; the rest is set when the prompt is shown
export const updateCesSurveySchema = z.object({
  score: cesSurveyScoreSchema,
  feedback: z.string().nullish(),
});
export type UpdateCesSurveyType = z.infer<typeof updateCesSurveySchema>;
